// MonthlyShift.jsx
import React, { useEffect, useState, useContext, useRef } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Button,
  Tooltip,
  Fab,
  IconButton,TextField, CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { LocalizationProvider ,DateTimePicker } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import itLocale from 'date-fns/locale/it';
import { ToastComponent } from '@syncfusion/ej2-react-notifications';
import { cicassService, auth } from '../../../_services';
import TokenContext from 'components/TokenContext';
import EventMobileCard from './EventMobileCard';
import { config } from "_helpers"; // Importato per il check responsive, come in Sportivi
import FieldFactory from './components/FieldFactory';
import CalendarButton from "../../../components/common/CalendarButton";


// Giorni della settimana in italiano, ordine da Lun a Dom
const daysOfWeek = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
const apiUrl = "dynamicshift";

const MonthlyShift = ({ year, month, t, targetEventId, onEventAutoOpened, currentAnaCalendarId , setCalendarName}) => {
  const theme = useTheme();
  const toastRef = useRef(null);
  const myRole = auth.getUserRole();
  const { jwt } = useContext(TokenContext);
  const currentUserId = auth.getUserId(jwt);
  const canEdit = ["Admin", "SuperUser", "Manager"].includes(myRole);

   // Form State specifico per l'entità Shift
  const [shiftsData, setShiftsData] = useState([]);
  const [dynamicFields, setDynamicFields] = useState([]); 
  const [calendarData,setCalendarData] = useState(null);
  const [dynamicValues ,setDynamicValues ] = useState({});
  const [lookupData, setLookupData] = useState({ roles: [], users: [] });
  const [unavailableUserIds, setUnavailableUserIds] = useState([]);
  const [adminNote, setAdminNote] = useState("");
  const [shiftFrom, setShiftFrom] = useState(null);
  const [shiftTo, setShiftTo] = useState(null);
  const [trucksCombo, setTrucksCombo] = useState([]);
  const [userFieldIds, setUserFieldIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  // Stati Gestione Modale (CRUD)
  const [selectedShift, setSelectedShift] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
   
  // Stati Mobile UX Uniforme
  const [mobileCardOpen, setMobileCardOpen] = useState(false);
  const [mobileCardData, setMobileCardData] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < config.mobileSizeCheck);
  
  const [toastInfo, setToastInfo] = useState({
    title: '',
    content: '',
    cssClass: 'e-toast-success',
    icon: 'e-success',
  });

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth < config.mobileSizeCheck);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const res = await cicassService.getAll(apiUrl + '/users/lookups');
        setLookupData(res.data); // Salva { roles: [], users: [] }
        } catch (e) {
        console.error("Errore caricamento utenti/ruoli", e);
        }
    };
    fetchLookups();
    }, []); // Eseguito una sola volta

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        const fieldsLoaded = await loadConfig();   
        await loadShifts(fieldsLoaded);  
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, [year, month]);
  
  useEffect(() => {
    if (!targetEventId) return; // non fare nulla se non c'è un target
    const openTarget = async () => {
      const fieldsLoaded = await loadConfig();
      await loadShifts(fieldsLoaded);
    };
    openTarget();
  }, [targetEventId]); // ← gestito separatamente
  
  const formatDateADD = (d) => {
  // Se d è una stringa, convertiamola in oggetto Date
  const dateObj = typeof d === 'string' ? new Date(d) : d;
  
  // Se la conversione fallisce o d è nullo, gestisci l'errore
  if (!(dateObj instanceof Date) || isNaN(dateObj)) return '';

  const pad = (n) => String(n).padStart(2, '0');
  return `${dateObj.getFullYear()}-${pad(dateObj.getMonth() + 1)}-${pad(dateObj.getDate())}`;
};


  const loadShifts = async (fields = []) => {
    try {
      // Carichiamo solo i dati dei turni, non serve ricaricare la config dei campi
      const eventsData = await cicassService.getAll(apiUrl + `/events/${currentAnaCalendarId}/year/${year}/month/${month + 1}`);
      setShiftsData(eventsData.data || []);
      if (targetEventId) {
            // Cerchiamo l'evento tra quelli appena scaricati e mappati
            const eventToOpen = eventsData.data.find(e => String(e.id) === String(targetEventId));
            if (eventToOpen) {
              
              await handleOpenDialog(new Date(eventToOpen.shiftFrom), eventToOpen,fields);
              // Notifichiamo alla pagina padre di resettare il targetEventId
              if (onEventAutoOpened) {
                onEventAutoOpened();
              }
            }
          }
    } catch (e) {
      console.error("Errore ricaricamento turni", e);
    }
  };

  const loadConfig = async () => {
    try {
      const configRes = await cicassService.getAll(apiUrl + '/config/' + currentAnaCalendarId);
      const trukRes = await cicassService.getAll('anatruck/combo');
      
      const sortedFields = configRes.data.fields.sort((a, b) => a.viewOrder - b.viewOrder);
      // Inizializzazione dei valori dinamici basata sui campi ricevuti
      const initialDynamicValues = {};
      sortedFields.forEach(field => {
        initialDynamicValues[field.id] = ""; 
      });
      const extractedUserIds = sortedFields
        .filter(field => field.isAnaUserIdCombo === 1)
        .map(field => field.id);
      setUserFieldIds(extractedUserIds);
      setTrucksCombo(trukRes.data);
      setCalendarData(configRes.data.calendarTemplate || null);
      setCalendarName(configRes.data.calendarName || "");
      setDynamicFields(sortedFields || []);
      if (!targetEventId) {
        setDynamicValues(initialDynamicValues);
      }
      return sortedFields;
    } catch (e) {
      console.error("Errore caricamento configurazione campi", e);
      return [];
    }
  };
  // Utenti non disponibili
  const fetchUnavailableUsers = async (dateFrom, dateTo,shiftId) => {
    if (!dateFrom || !dateTo) return;
    try {
        const pad = (n) => String(n).padStart(2, '0');

        const formattedDateFrom = `${dateFrom.getFullYear()}${pad(dateFrom.getMonth() + 1)}${pad(dateFrom.getDate())}${pad(dateFrom.getHours())}${pad(dateFrom.getMinutes())}`;
        const formattedDateTo   = `${dateTo.getFullYear()}${pad(dateTo.getMonth() + 1)}${pad(dateTo.getDate())}${pad(dateTo.getHours())}${pad(dateTo.getMinutes())}`;

        const response = await cicassService.getAll(`${apiUrl}/users/vacation/${formattedDateFrom}/to/${formattedDateTo}/shiftId/${shiftId}`);
        if (response && response.data) {
          setUnavailableUserIds(response.data);
        }
      } catch (error) {
      console.error("Errore nel recupero degli utenti non disponibili:", error);
      setUnavailableUserIds([]); // In caso di errore svuotiamo per sicurezza
    }
  };
  // Gestione TOAST
  const showToast = (title, content, cssClass) => {
    setToastInfo({
      title: title,
      content: content,
      cssClass: cssClass, // "e-toast-success" per il verde, "e-toast-danger" per il rosso
      icon: cssClass === "e-toast-success" ? "e-success-toast-icon" : "e-error-toast-icon"
    });
    
    // Piccolo timeout per dare tempo allo stato di aggiornarsi prima di mostrare il toast
    setTimeout(() => {
      if (toastRef.current) {
        toastRef.current.show();
      }
    }, 100);
  };

  // Costruzione della griglia del calendario come in Sportivi
  const firstDay = new Date(year, month, 1);
  const startDay = (firstDay.getDay() + 6) % 7;
  const daysInMonthCount = new Date(year, month + 1, 0).getDate();
  const weeks = [];
  for (let w = 0, d = 1; w < 6; w++) {
    const week = [];
    for (let i = 0; i < 7; i++) {
      if ((w === 0 && i < startDay) || d > daysInMonthCount) week.push(null);
      else week.push(d++);
    }
    weeks.push(week);
  }

  // Raggruppamento per data 
    const shiftsByDate = {};
    if (shiftsData && Array.isArray(shiftsData)) {
    shiftsData.forEach(ev => {
        // Aggiungi anche un check se ev.shiftFrom esiste per sicurezza
        if (ev.shiftFrom) {
        const key = new Date(ev.shiftFrom).toDateString();
        (shiftsByDate[key] ||= []).push(ev);
        }
    });
    }

  // Apertura form/dettaglio turno (adattato per ricevere "date" come Sportivi)
  const handleOpenDialog = async (date, shift = null, fieldsOverride = null) => {
    const fields = fieldsOverride ?? dynamicFields; 
    /*console.log("=== handleOpenDialog ===");
    console.log("fieldsOverride:", fieldsOverride);
    console.log("dynamicFields (stato):", dynamicFields);
    console.log("fields usati:", fields);*/
    if (shift) {
      // --- LOGICA EDIT ---
      const eventData = await cicassService.getAll(apiUrl + '/event/' + shift.id);
      var reloadData = eventData.data;
      /*console.log("reloadData:", reloadData);
      console.log("reloadData.dynamicValues:", reloadData.dynamicValues);*/
      setSelectedShift(reloadData);
      setSelectedEventId(reloadData.id);
      setAdminNote(reloadData.adminNote || "");
      setShiftFrom(new Date(reloadData.shiftFrom));
      setShiftTo(new Date(reloadData.shiftTo));
      fetchUnavailableUsers(new Date(reloadData.shiftFrom),new Date(reloadData.shiftTo),reloadData.id);

      const updatedDynamicValues = {};
      const savedValuesArray = reloadData.dynamicValues || [];

      fields.forEach(field => {
        // Cerchiamo il valore salvato nel contenitore dei valori dinamici
        const foundField = savedValuesArray.find(v => v.anaFieldId === field.id);

        if (field.componentType === 'Autocomplete' && field.isAnaUserIdCombo === 1) {
          // CASO COMBO UTENTI: Prendi l'ID (valore stringa) e convertilo in numero
          if (foundField && foundField.value) {
            updatedDynamicValues[field.id] = parseInt(foundField.value, 10);
          } else {
            updatedDynamicValues[field.id] = null;
          }
        } 
        else if (field.componentType === 'DatePicker' || field.componentType === 'TimePicker') {
          // CASO DATE
          updatedDynamicValues[field.id] = (foundField && foundField.value) ? new Date(foundField.value) : null;
        } 
        else {
          // CASO TUTTI GLI ALTRI (Testo, Statici, etc.)
          updatedDynamicValues[field.id] = (foundField && foundField.value !== undefined && foundField.value !== null) 
                                           ? foundField.value 
                                           : "";
        }
      });
      //console.log("updatedDynamicValues finale:", updatedDynamicValues);
      setDynamicValues(updatedDynamicValues);
      setIsDialogOpen(true);
    } else {
      // --- LOGICA ADD ---
      const baseDateFrom = new Date(date);
      baseDateFrom.setHours(8, 0, 0, 0); // Inizio alle 08:00
      const baseDateTo = new Date(date);
      baseDateTo.setHours(9, 0, 0, 0);   // Fine alle 09:00 (+1 ora)
      setShiftFrom(baseDateFrom);
      setShiftTo(baseDateTo);
      setSelectedShift(null);
      setSelectedEventId(0);
      setAdminNote("");
      fetchUnavailableUsers(baseDateFrom, baseDateTo, 0);
      // Inizializzazione dinamica basata su ciò che hai in dynamicFields (che avevi già caricato)
      // dynamicFields è il tuo array di oggetti che contiene l'ID del campo
      const initialDynamicValues = {};
      dynamicFields.forEach(field => {
        if (field.componentType === 'DatePicker' && date) {
          initialDynamicValues[field.id] = date;
        } else {
          initialDynamicValues[field.id] = ""; 
        }
      });
      
      setDynamicValues(initialDynamicValues);

      setIsDialogOpen(true);
    }
  };

  const handleClose = () => {
      setShiftFrom(null);
      setShiftTo(null);
      setAdminNote("");
      setIsDialogOpen(false);

      // 2. Pulizia: chiamiamo la funzione passata dal padre
      if (onEventAutoOpened) {
          onEventAutoOpened();
      }
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      // 2️⃣ Costruiamo il Dictionary piatto FormValues richiesto dall'API C#
      const formValuesMap = {};
      
      if (dynamicValues) {
        Object.keys(dynamicValues).forEach(key => {
          const value = dynamicValues[key];
          if (value !== undefined && value !== null) {
            formValuesMap[key] = value.toString();
          }
        });

      }

      // FIX TIMEZONE: Se start è una stringa, gestisci il timezone
      let fixedFrom,fixedTo;
      if (typeof shiftFrom === 'string') {
        // Per stringhe datetime, rimuovi la 'Z' o timezone info e aggiungi offset locale
        const localDateFrom = new Date(shiftFrom);
        const localDateTo = new Date(shiftTo);
        fixedFrom = new Date(localDateFrom.getTime() - (localDateFrom.getTimezoneOffset() * 60000));
        fixedTo = new Date(localDateTo.getTime() - (localDateTo.getTimezoneOffset() * 60000));
      } else {
        // Se è già un Date object
        fixedFrom = new Date(shiftFrom.getTime() - (shiftFrom.getTimezoneOffset() * 60000));
        fixedTo = new Date(shiftTo.getTime() - (shiftTo.getTimezoneOffset() * 60000));
      }
      const payload = {
        id: selectedEventId,                                 // Inizializzato a -1 se è un nuovo turno
        anaCalendarId: currentAnaCalendarId,           // Preso dalle props del componente
        adminNote: adminNote,   // SERVIZIO
        anaUserId: parseInt(currentUserId) || 0, 
        shiftFrom: fixedFrom,  
        shiftTo: fixedTo,      
        formValues: formValuesMap                      // Il Dictionary<string, string> piatto
      };
      await cicassService.simplePost(apiUrl + "/save", payload);

      // 5️⃣ Feedback visivo di successo tramite il Toast di Syncfusion
      if (selectedEventId == 0) {
        showToast("Success", t('addToastOK') || "Turno pianificato con successo", "e-toast-success");
      } else {
        showToast("Success", t('editToastOK') || "Turno aggiornato con successo", "e-toast-success");
      }

      // Chiudiamo la modale e forziamo il ricaricamento del tabellone
      setIsDialogOpen(false);
      await loadShifts();

    } catch (error) {
      console.error("Errore durante l'esecuzione di handleSave: ", error);

      // Gestione ed estrazione del BadRequest personalizzato del backend
      let errorMessage = "Impossibile salvare il turno. Verificare i dati inseriti.";
      
      if (error.response && error.response.data) {
        errorMessage = typeof error.response.data === 'string' 
          ? error.response.data 
          : (error.response.data.message || JSON.stringify(error.response.data));
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Mostra il popup di errore bloccante in rosso
      showToast("Blocco Pianificazione", errorMessage, "e-toast-danger");

    } finally {
      setIsLoading(false);
    }
  };
  const handleDeleteConfirmed = async () => {
    if (!selectedShift) return;
    setIsLoading(true);
    try {
      await cicassService.deleteOp(apiUrl, selectedShift.id);
      setDeleteDialogOpen(false);
      setSelectedShift(null);
      setIsDialogOpen(false);
      await loadShifts();
      showToast(t('success'), t('deleteToastOK'), 'e-toast-warning', 'e-warning toast-icons');
    } catch (err) {
      console.error('Errore eliminazione evento:', err);
      showToast(t('error'), t('erroreEliminazione'), 'e-toast-danger', 'e-error');
    
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} locale={itLocale}>
      <Box sx={{ position: 'relative', p: 2 }}>
        {isLoading && (
          <Box sx={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            bgcolor: 'rgba(255,255,255,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
            borderRadius: 1
          }}>
            <CircularProgress />
          </Box>
        )}
        {canEdit && (
          <Tooltip title={t('Aggiungi Turno')} placement="top">
            <Fab color="primary" sx={{ position: 'fixed', bottom: 80, right: 20, zIndex: 1000 }} onClick={() => handleOpenDialog(new Date(year, month, 1))}>
              <AddIcon />
            </Fab>
          </Tooltip>
        )}

        {/* HEADER GIORNI SETTIMANA (Solo Desktop) */}
        {!isMobile &&
        <Grid container>
          {daysOfWeek.map((d, i) => (
            <Grid item xs key={i}>
              <Typography
                align="center"
                sx={{ fontWeight: (d === 'Sab' || d === 'Dom') ? 'bold' : 'normal' }}
              >
                {d}
              </Typography>
            </Grid>
          ))}
        </Grid>}

        {isMobile ? (
          /* GRIGLIA MOBILE LIST */
          <Box sx={{ minWidth: '336px' }}>
            {weeks.flatMap(week => week).map((day, idx) => (
              day && (
                <Box key={idx} sx={{ mb: 1, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {day} {new Date(year, month, day).toLocaleDateString('it-IT', { weekday: 'long' })}
                  </Typography>
                  {shiftsByDate[new Date(year, month, day).toDateString()]?.map(shift => {
                    // Stile chip coerente con Sportivi (usiamo lo stato per determinare il colore)
                    const isMine = shift.dynamicValues && shift.dynamicValues.some(sv => {
                            const isUserField = userFieldIds.includes(sv.anaFieldId);
                            const isCurrentUser = Number(sv.value) === Number(currentUserId);
                            return isUserField && isCurrentUser;
                          });
                    const bg = isMine ? "#ABEBC6" : "#2c5282";
                    const textColor = isMine ? "#2c5282" : "#FFFFFF"; 

                    return (
                      <Box key={shift.id}
                        onClick={e => {
                          e.stopPropagation();
                          handleOpenDialog(new Date(shift.shiftFrom), shift);
                        }}
                        sx={{
                          mb: 1,
                          p: 1,
                          borderRadius: 1,
                          bgcolor: bg,
                          color: textColor,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: 'action.selected',
                            color: "#2c5282",
                            '& *': {
                              color: "#2c5282",
                            }
                          },
                        }}
                      >
                        <Typography noWrap sx={{ color: textColor }}>
                          {new Date(shift.shiftFrom).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})} - {new Date(shift.shiftTo).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}
                        </Typography>
                        {shift.adminNote && <Typography noWrap variant="body2" sx={{ color: textColor }}>{shift.adminNote}</Typography>}
                      </Box>
                    );
                  })}
                </Box>
              )
            ))}
          </Box>
        ) : (
          /* GRIGLIA DESKTOP (Uniforme a Sportivi) */
          <Box sx={{ overflowX: 'auto' }}>
            {weeks.map((week, wi) => (
              <Box
                key={wi}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                  gap: 1,                  
                  mb: 1,                   
                }}
              >
                {week.map((day, di) => {
                  const dateObj = day ? new Date(year, month, day) : null;
                  const key = dateObj?.toDateString() || '';
                  return (
                    <Paper
                      key={di}
                      variant="outlined"
                      square
                      onClick={() => dateObj && handleOpenDialog(dateObj)}
                      sx={{
                        minHeight: { xs: 80, sm: 100 },
                        p: 1,
                        bgcolor: dateObj ? 'inherit' : theme.palette.action.hover,
                        cursor: dateObj ? 'pointer' : 'default',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      {day && (
                        <Typography
                          sx={{
                            fontWeight: [0, 6].includes(new Date(year, month, day).getDay())
                              ? 'bold'
                              : 'normal',
                          }}
                        >
                          {day}
                        </Typography>
                      )}
                      
                      {shiftsByDate[key]?.map(shift => {
                        const isMine = shift.dynamicValues && shift.dynamicValues.some(sv => {
                            const isUserField = userFieldIds.includes(sv.anaFieldId);
                            const isCurrentUser = Number(sv.value) === Number(currentUserId);
                            return isUserField && isCurrentUser;
                          });
                        const bg = isMine ? "#ABEBC6" : "#2c5282";
                        const textColor = isMine ? "#2c5282" : "#FFFFFF"; 
                        return (
                          <Box
                            key={shift.id}
                            onClick={e => { e.stopPropagation(); handleOpenDialog(new Date(shift.shiftFrom), shift); }}
                            sx={{
                              mt: 0.5,
                              p: 1,
                              borderRadius: 1,
                              bgcolor: bg,
                              color: textColor,
                              cursor: 'pointer',
                              display: 'inline-block',
                              maxWidth: '100%',
                              lineHeight: 1.2,
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                bgcolor: 'action.selected',
                                color: '#2c5282',
                                '& *': {
                                  color: '#2c5282',
                                },
                              },
                            }}
                          >
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: textColor }} noWrap>
                            {shift.adminNote}
                          </Typography>
                          <Typography variant="body2"  sx={{ color: textColor }}>
                              {new Date(shift.shiftFrom).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})} - {new Date(shift.shiftTo).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}
                          </Typography>

                        </Box>
                        );
                      })}
                    </Paper>
                  );
                })}
              </Box>
            ))}
          </Box>
        )}

        {/* DIALOG GESTIONE ED EDITING TURNO (Layout stile Sportivi: flex column) */}
        {isDialogOpen && (
          <Dialog open={isDialogOpen} onClose={handleClose} fullWidth keepMounted={false}>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {canEdit 
                ? (selectedEventId && selectedEventId !== -1 ? t('Modifica') : t('Nuovo')) 
                : t('Visualizza')
              } {t('Servizio')}
              {selectedShift && selectedShift.id > 0 && (
                <CalendarButton
                    data={{
                      title: 'Servizio',
                      start: new Date(selectedShift.shiftFrom),
                      end: new Date(selectedShift.shiftTo),
                      description: selectedShift.adminNote || '',
                      location: ''
                    }}
                    filename={`servizio_${selectedShift.id}.ics`}
                    t={t}
                    buttonProps={{
                      sx: {
                        color: "#2c5282",
                        "&:hover": {
                          backgroundColor: "#2c5282",
                          color: "#fff",
                        },
                      }
                    }}
                  />
            )}
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Divider />
            
            <Grid container spacing={2}>
                {/* IL CAMPO INCHIODATO */}
                <Grid item xs={12} key="AdminNoteKey">
                  <TextField
                  fullWidth
                  disabled={!canEdit}
                  label={t('Servizio')}
                  value={adminNote || ''}
                  onChange={(e) => setAdminNote(e.target.value)}
                  required
                />
                </Grid>
                <Grid item xs={12} sm={6} key="ShiftFromKey">
                  <DateTimePicker
                    disabled={!canEdit}
                    label={t('Inizio Servizio')}
                    value={shiftFrom}
                    onChange={(newFrom) => {
                        if (!newFrom) { setShiftFrom(null); return; } // anche fix Bug 4
                        setShiftFrom(newFrom);

                        let newTo = shiftTo;
                        if (!shiftTo || shiftTo < new Date(newFrom.getTime() + 60 * 60 * 1000)) {
                            newTo = new Date(newFrom.getTime() + 60 * 60 * 1000);
                            setShiftTo(newTo);
                        }
                        fetchUnavailableUsers(newFrom, newTo, selectedEventId);
                    }}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth size="small" required />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6} key="ShiftToKey">
                  <DateTimePicker
                    disabled={!canEdit}
                    label={t('Fine Servizio')}
                    value={shiftTo}
                    onChange={(newTo) => {
                        if (!newTo) { setShiftTo(null); return; }
                        setShiftTo(newTo);
                        if (shiftFrom) {
                            fetchUnavailableUsers(shiftFrom, newTo, selectedEventId);
                        }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        required
                        error={!!(shiftFrom && shiftTo && shiftFrom >= shiftTo)}
                        helperText={
                          shiftFrom && shiftTo && shiftFrom >= shiftTo
                            ? t('Fine deve essere successiva all\'inizio')
                            : ''
                        }
                      />
                    )}
                  />
                </Grid>
                {/* MOTORE DINAMICO */}
                {dynamicFields.map((field) => (
                <Grid item xs={12} key={field.id}>
                    <FieldFactory 
                        field={field} 
                        value={dynamicValues[field.id]} 
                        dynamicValues={dynamicValues}
                        unavailableUserIds={unavailableUserIds}
                        onChange={(val) => setDynamicValues({ ...dynamicValues, [field.id]: val })} 
                        lookupData={lookupData}
                        trucksList={trucksCombo}
                        disabled={!canEdit}
                    />
                </Grid>
                ))}
            </Grid>
            </DialogContent>
            <DialogActions>
              {selectedShift && canEdit && (
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 'auto', ml: 2 }}>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteDialogOpen(true);
                    }}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              )}
              <Button onClick={handleClose}>{canEdit ? t('annulla') : t('chiudi')}</Button>
              {(() => {
                // Il form è invalido se ALMENO UN campo obbligatorio è vuoto
                const isFormInvalid = (!adminNote || adminNote.trim() === '') ||
                   !shiftFrom || !shiftTo || shiftFrom >= shiftTo ||
                  dynamicFields.some(field => {
                  if (field.isMandatory === 1) {
                    const val = dynamicValues[field.id];
                    // Controlla se il valore è null, undefined o stringa vuota/spazi
                    return val === undefined || val === null || (typeof val === 'string' && val.trim() === '');
                  }
                  return false;
                });
                if (canEdit)
                {
                  return (
                  <Button 
                    onClick={handleSave} 
                    variant="contained" 
                    color="primary"
                    disabled={isFormInvalid} // 🚀 Disabilita se mancano campi obbligatori
                  >
                    {t('salva')}
                  </Button>
                );
                }
                
              })()}
            </DialogActions>
          </Dialog>
        )}

        {/* DIALOG CONFERMA ELIMINAZIONE */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} fullWidth maxWidth="xs">
          <DialogTitle>{t('confirmDelete')}</DialogTitle>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>{t('cancel')}</Button>
            <Button onClick={handleDeleteConfirmed} variant="contained" color="primary">
              {t('delete')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* NOTIFICHE TOAST */}
        <ToastComponent
          ref={toastRef}
          title={toastInfo.title}
          content={toastInfo.content}
          cssClass={toastInfo.cssClass}
          icon={toastInfo.icon}
          position={{ X: 'Right', Y: 'Bottom' }}
        />

        {/* COMPONENTE RESPONSIVE MOBILE DI VISUALIZZAZIONE */}
        {!canEdit && mobileCardData && (
          <EventMobileCard
            open={mobileCardOpen}
            onClose={() => setMobileCardOpen(false)}
            data={mobileCardData}
            t={t}
          />
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default MonthlyShift;